import { useLingui } from '@lingui/react/macro';
import { Braces, DatabaseZap, RefreshCw } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';

import { useActionDialogs, type ActionDefinition } from '@/shared/action';
import { formatInstant } from '@/shared/format/date';
import { useIsMobile } from '@/shared/mobile';
import { EmptyState } from '@/shared/page';
import { resolvePatternText, type PatternText } from '@/shared/patterns';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog';

/** Estado do snapshot como o legado mostrava (`DatasetWrapper`): mensagem amigável por status. */
export type DatasetState = 'ok' | 'empty' | 'processing' | 'unavailable' | 'timeout' | 'error';

/**
 * Nova consulta ao bureau. É `write` com prévia que mostra o custo (U24, X01): só humano, pelo
 * diálogo; nunca vira ferramenta de agente nem ação `page: true`.
 */
export interface DatasetRefresh<C, V extends FieldValues = FieldValues, R = unknown> {
  readonly action: ActionDefinition<C, V, R>;
  readonly ctx: C;
  /** Sem a permissão de consulta (`RequestDataSet`): o botão aparece desabilitado com o motivo. */
  readonly disabled?: boolean | PatternText;
}

export interface DatasetPanelProps<C, V extends FieldValues = FieldValues, R = unknown> {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** Instante ISO de quando o snapshot foi gerado. */
  readonly generatedAt?: string | null | undefined;
  readonly state?: DatasetState;
  readonly refresh?: DatasetRefresh<C, V, R>;
  /** O JSON cru, para o "Valores originais" do legado. */
  readonly raw?: unknown;
  /** A leitura do snapshot (tabela, `DescriptionList` ou apresentação própria). */
  readonly children?: ReactNode;
  readonly className?: string;
}

/**
 * Painel de consulta (P19, 06 §3.b): lê o snapshot já gerado ("Gerado em …"), mostra a mensagem do
 * estado e oferece "Atualizar", que é uma consulta **paga**: passa pelo `ActionDialog` com a prévia
 * do custo, sempre com confirmação humana (X01).
 */
export function DatasetPanel<C, V extends FieldValues = FieldValues, R = unknown>({ title, description, generatedAt, state = 'ok', refresh, raw, children, className }: DatasetPanelProps<C, V, R>) {
  const { i18n, t } = useLingui();
  const isMobile = useIsMobile();
  const dialogs = useActionDialogs();
  const [rawOpen, setRawOpen] = useState(false);
  const headingId = useId();
  if (import.meta.env.DEV && refresh && refresh.action.kind !== 'write') throw new Error(`DatasetPanel: ${refresh.action.id} precisa ser write (consulta paga, X01)`);

  const generated = generatedAt ? formatInstant(generatedAt, { locale: i18n.locale }) : null;
  const disabled = refresh?.disabled !== undefined && refresh.disabled !== false;
  const reason = refresh && (typeof refresh.disabled === 'string' || typeof refresh.disabled === 'object') ? resolvePatternText(i18n, refresh.disabled) : undefined;
  const request = () => {
    if (refresh && !disabled) dialogs.open(refresh.action, refresh.ctx);
  };
  const messages: Partial<Record<DatasetState, { tone: 'info' | 'warn' | 'err'; text: string }>> = {
    processing: { tone: 'info', text: t`A consulta ainda está em processamento. Atualize a página em alguns minutos.` },
    unavailable: { tone: 'warn', text: t`A fonte não tem dados para este registro.` },
    timeout: { tone: 'warn', text: t`A fonte demorou demais para responder. Tente de novo mais tarde.` },
    error: { tone: 'err', text: t`A consulta falhou na fonte. Tente de novo mais tarde.` },
  };
  const message = messages[state];

  return (
    <section aria-labelledby={headingId} data-slot="dataset-panel" className={cn('flex flex-col gap-4 rounded-card border border-border bg-card p-4 md:p-5', className)}>
      <header className={cn('flex gap-3', isMobile ? 'flex-col' : 'flex-wrap items-start')}>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h2 id={headingId} className="text-base font-semibold text-foreground">
            {title}
          </h2>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
          {generated ? <p className="text-sm text-muted-foreground tabular-nums">{t`Gerado em ${generated}`}</p> : null}
        </div>
        <div className={cn('flex gap-2', isMobile && '[&>*]:flex-1')}>
          {raw !== undefined && state !== 'empty' ? (
            <Button type="button" variant="secondary" onClick={() => setRawOpen(true)}>
              <Braces aria-hidden />
              {t`Valores originais`}
            </Button>
          ) : null}
          {refresh && state !== 'empty' ? (
            <Button type="button" variant="secondary" aria-disabled={disabled || undefined} {...(reason ? { title: reason } : {})} onClick={request}>
              <RefreshCw aria-hidden />
              {t`Atualizar`}
            </Button>
          ) : null}
        </div>
      </header>
      {reason && state !== 'empty' ? <p className="-mt-2 text-sm text-muted-foreground">{reason}</p> : null}
      {message ? <Alert tone={message.tone}>{message.text}</Alert> : null}
      {state === 'empty' ? (
        <EmptyState
          compact
          icon={DatabaseZap}
          title={t`Nenhuma consulta feita ainda`}
          description={reason ?? t`A consulta é paga e fica guardada para as próximas leituras.`}
          {...(refresh
            ? {
                action: (
                  <Button type="button" aria-disabled={disabled || undefined} onClick={request}>
                    <DatabaseZap aria-hidden />
                    {t`Obter dados`}
                  </Button>
                ),
              }
            : {})}
        />
      ) : (
        children
      )}
      {raw !== undefined ? (
        <Dialog open={rawOpen} onOpenChange={setRawOpen}>
          <DialogContent size="form">
            <DialogHeader>
              <DialogTitle>{t`Valores originais`}</DialogTitle>
              <DialogDescription>{t`O que a fonte devolveu, sem tratamento.`}</DialogDescription>
            </DialogHeader>
            <pre className="max-h-[60dvh] overflow-auto rounded-md bg-sunk p-3 font-mono text-xs text-foreground">{JSON.stringify(raw, null, 2)}</pre>
          </DialogContent>
        </Dialog>
      ) : null}
      {dialogs.element}
    </section>
  );
}
